<template>
  <fin-my-profile-block title="Deine DSGVO Anfragen">
    <p>Hier kannst du eine Auskunft über deine personenbezogenen Daten herunterladen.</p>
    <v-btn :disabled="disableGdprBtn"
           class="mb-8 pa-0"
           text
           @click="downloadReport">
      <v-icon class="mr-2">icon-arrow-link-down</v-icon>
      DSGVO-Bericht herunterladen
    </v-btn>
    <fin-logo-full v-if="disableGdprBtn"
                   :loading="disableGdprBtn"
                   :size="167"
                   class="ml-3 mt-n8"
                   dark/>
    <p>Falls Du dein Konto löschen möchtest, kannst Du es hier durchführen. Hiermit werden alle deine
      Daten gelöscht. Du kannst dich natürlich jederzeit neu registrieren.</p>
    <v-btn class="pa-0"
           text
           @click="askDeleteUserAccountConfirmation()">
      <v-icon class="mr-2">icon-delete</v-icon>
      Konto löschen
    </v-btn>

    <fin-dialog v-model="showReportInAppDisabledDialog">
      <template #title>
        DSGVO-Bericht
      </template>
      <div class="mb-4">
        Diese Funktionalität ist nur über die Web-Version verfügbar. Um einen DSGVO-Bericht zu erzeugen,
        besuche <a :href="`${baseUrl}/protected/myProfile`">finCraft.de</a>.
      </div>
    </fin-dialog>

    <fin-dialog v-model="openInvoice"
                :max-width="750">
      <template #title>
        Problem beim Löschen deines Kontos
      </template>
      <template>
        <v-card-text class="px-0" style="line-height: 23px;">
          <template v-if="this.paymentStatus === 'open' && this.groupPriceModel.id === 'ADVANCED'">
            Du kannst dein Konto leider nicht löschen, da noch offene Rechnungen vorliegen und du das Abo
            <em>Advanced</em> nutzt.
          </template>
          <template v-else-if="this.paymentStatus === 'open' && this.groupPriceModel.id === 'EXTENDED'">
            Du kannst dein Konto leider nicht löschen, da noch offene Rechnungen vorliegen und du das Abo
            <em>Extended</em> nutzt.
          </template>
          <template v-else-if="this.paymentStatus !== 'open' && this.groupPriceModel.id === 'EXTENDED'">
            Du kannst dein Konto leider nicht löschen, da du das Abo <em>Extended</em> nutzt.
          </template>
          <template v-else-if="this.paymentStatus !== 'open' && this.groupPriceModel.id === 'ADVANCED'">
            Du kannst dein Konto leider nicht löschen, da du das Abo <em>Advanced</em> nutzt.
          </template>
          <template v-else>
            Du kannst dein Konto leider nicht löschen, da noch offene Rechnungen vorliegen.
          </template>
          <br/>
          Falls du Hilfe brauchst, kontaktiere uns: <a href="mailto:support@fincraft.de">support@fincraft.de</a>
        </v-card-text>
      </template>
    </fin-dialog>

  </fin-my-profile-block>
</template>

<script>
import {finUserAPI} from "@/js/finAPI"
import {mapActions, mapGetters} from "vuex"
import FinMyProfileBlock from "@/components/pages/profile/MyProfileBlock"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {createGdprReport} from "@/components/pages/profile/graphql"
import FinLogoFull from "@/components/common/logo/LogoFull"
import finBrowser from "@/js/finBrowser"
import BaseUrl from "@/mixins/BaseUrl"

export default {
  name: 'FinGdprRequests',
  components: {FinLogoFull, FinMyProfileBlock},
  props: {
    paymentStatus: {
      type: String
    },
  },
  data() {
    return {
      openInvoice: false,
      deleteUserDialog: false,
      disableGdprBtn: false,
      showReportInAppDisabledDialog: false,
      baseUrl: undefined
    }
  },
  mixins: [BaseUrl],
  computed: {
    ...mapGetters('auth', ['userId', 'profile', 'groupPriceModel']),
  },
  methods: {
    ...mapActions('auth', ['signOut']),
    askDeleteUserAccountConfirmation() {
      this.$confirm({
        title: 'Konto löschen',
        text: `Möchtest du dein Konto wirklich löschen? Hiermit werden alle deine Daten gelöscht.`,
        dialogWidth: 448,
        confirmBtn: 'Konto löschen'
      })
        .then(() => {
          this.deleteUserAccount()
        })
    },
    async deleteUserAccount() {
      if (this.paymentStatus === 'open' || this.groupPriceModel.id === 'ADVANCED' || this.groupPriceModel.id === 'EXTENDED') {
        this.openInvoice = true
      } else {
        try {
          await finUserAPI.deleteUser(this.userId)

          await this.signOut()

          await this.$router.push('/')
          this.$toasted.global.finToast({
            message: 'Dein Konto wurde gelöscht.',
            type: 'success'
          })
        } catch (error) {
          console.error("your account " + this.userId + " could not be deleted", error)
          this.$toasted.global.finToast({
            message: 'Dein Konto konnte nicht gelöscht werden.',
            type: 'error'
          })
        }
      }
    },
    async downloadReport() {
      if (finBrowser.IS_IOS_OR_ANDROID_APP) {
        this.showReportInAppDisabledDialog = true
      } else {
        this.disableGdprBtn = true
        try {
          const userReportData = await API.graphql(graphqlOperation(createGdprReport, {
            userId: this.userId
          }))

          if (userReportData.data.createUserDataReport) {
            const now = new Date()
            const dateString = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
              .toISOString()
              .split("T")[0]

            let filename = this.profile.username

            if (this.profile.firstname && this.profile.lastname) {
              filename = this.profile.firstname + this.profile.lastname
            }

            const a = document.createElement("a")
            a.href = URL.createObjectURL(new Blob(
              [JSON.stringify(userReportData.data.createUserDataReport, null, 2)],
              {
                type: "text/plain"
              }
            ))
            a.setAttribute('download', dateString + '_DSGVO_Report_' + filename + '.txt')

            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }
        } catch (error) {
          console.error("MyProfile: GdprRequests could not generate report", error)
          this.$toasted.global.finToast({
            message: 'Der DSGVO-Bericht konnte nicht erstellt werden. Bitte versuche es später noch einmal oder wende dich an&nbsp;<a href="mailto:support@fincraft.de">support@fincraft.de</a>.',
            type: 'error'
          })
        }

        this.disableGdprBtn = false
      }
    }
  },
  mounted() {
    this.baseUrl = this.getBaseURL()
  }
}
</script>

<style lang="scss" scoped>

</style>
